import React, { useRef } from 'react';
import { toast } from 'sonner';

/**
 * Mobile-friendly link component that behaves like WhatsApp:
 * - Tap: opens link in a new tab
 * - Long-press (hold >= 450ms): copies link to clipboard, vibrates, and shows toast
 * - Drag/scroll: cancels long-press so normal chat scrolling is unaffected
 */
export const WhatsAppLink = ({ url, children, className = '' }) => {
  const timerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });
  const isLongPressRef = useRef(false);

  const fullUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = fullUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      if (navigator.vibrate) {
        navigator.vibrate(40);
      }
      toast.success('Link copied', {
        duration: 1500,
        style: {
          background: 'rgba(37,211,102,0.95)',
          color: '#fff',
          fontSize: '13px',
          padding: '6px 16px',
          minHeight: 'auto'
        }
      });
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    startPosRef.current = { x: touch.clientX, y: touch.clientY };
    isLongPressRef.current = false;

    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      copyToClipboard();
    }, 450);
  };

  const handleTouchMove = (e) => {
    if (!timerRef.current || !e.touches || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - startPosRef.current.x);
    const dy = Math.abs(touch.clientY - startPosRef.current.y);
    // If finger moves more than 10px, treat as scroll and cancel long-press
    if (dx > 10 || dy > 10) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTouchEnd = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (isLongPressRef.current) {
      e.preventDefault();
      isLongPressRef.current = false;
      return;
    }
  };

  const handleContextMenu = (e) => {
    if (isLongPressRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-wa-link underline break-all ${className}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      {children || url}
    </a>
  );
};

// Render the WhatsApp-style subset used by chat and configured auto messages.
// Text is kept as React nodes instead of HTML so message content cannot inject markup.
export const renderMessageText = (value) => {
  const text = String(value ?? '');
  const tokenPattern = /(\*[^*\n]+\*|https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const parts = text.split(tokenPattern);

  const isUrl = (val) => /^https?:\/\//i.test(val) || /^www\./i.test(val);

  const renderInlinePart = (part, keyPrefix) => {
    if (isUrl(part)) {
      return (
        <WhatsAppLink key={keyPrefix} url={part}>
          {part}
        </WhatsAppLink>
      );
    }
    return part;
  };

  return parts.map((part, index) => {
    if (!part) return null;

    if (/^\*[^*\n]+\*$/.test(part)) {
      const inner = part.slice(1, -1);
      return <strong key={`bold-${index}`}>{renderInlinePart(inner, `bold-url-${index}`)}</strong>;
    }

    if (isUrl(part)) return renderInlinePart(part, `url-${index}`);

    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
  });
};
